import './styles.sass';

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import NumberHelper from 'util/number_helper';
import * as QueryFetcher from 'components/Explorer/models/query_fetcher';
import FilterSet from 'components/Explorer/models/filter_set';
import Editor from './Editor';
import HelpLink from '../../HelpLink';
import useQueryParams from 'hooks/useQueryParams';

const ANALYTICS_DIALOGUE_NAME = 'PubMed query';

const PubmedFilter = ({ filterSet }) => {
  const params = useQueryParams();

  const dialogRef = useRef(null);
  const newButtonRef = useRef(null);
  const editButtonRef = useRef(null);

  const searchID = filterSet.get('pubmed_search_id');

  const [showEditor, setShowEditor] = useState(false);
  const [pubmedSearch, setPubmedSearch] = useState(null);
  const [loading, setLoading] = useState(Boolean(searchID));

  // Hydrate from the URL once on mount. Subsequent save/clear update the
  // local state directly — re-firing on `searchID` would race against the
  // save's setPubmedSearch and overwrite it with a stale hydration response.
  useEffect(() => {
    if (!searchID) return undefined;

    setLoading(true);
    const request = QueryFetcher.fetchJSON('filters/pubmed_search', params);

    request.then((err, response) => {
      if (err?.name === 'AbortError') return;
      if (!err && response.body) {
        setPubmedSearch(response.body);
      }
      setLoading(false);
    });

    return () => request.abort();
  }, []);

  const openEditor = () => {
    setShowEditor(true);
    dialogRef.current?.showModal();
  };

  const closeEditor = () => {
    setShowEditor(false);
    dialogRef.current?.close();
  };

  // Defer to the next microtask so React has time to render the new buttons
  // before we move focus.
  const refocus = (next) => {
    queueMicrotask(() => {
      if (next) {
        editButtonRef.current?.focus();
      } else {
        newButtonRef.current?.focus();
      }
    });
  };

  const onClickClear = () => {
    filterSet.clear('pubmed_search_id');
    setPubmedSearch(null);
    refocus(null);

    Analytics.trackEvent('Advanced Search: Modal Dialogue Cleared', {
      dialogue_name: ANALYTICS_DIALOGUE_NAME
    });
  };

  const onCancelEdit = () => {
    closeEditor();
    refocus(pubmedSearch);
  };

  const onSaveEdit = (next) => {
    filterSet.set('pubmed_search_id', next.id);
    closeEditor();
    setPubmedSearch(next);
    refocus(next);
  };

  const descriptionString = () => {
    if (pubmedSearch.count === 0) {
      return I18n.t('Explorer.AdvancedSearch.fields.pubmed.empty');
    }
    if (pubmedSearch.total > pubmedSearch.count) {
      return I18n.htmlSafe('Explorer.AdvancedSearch.fields.pubmed.too_many', {
        count: NumberHelper.formatNumberWithDelimiter(pubmedSearch.count),
        total: NumberHelper.formatNumberWithDelimiter(pubmedSearch.total)
      });
    }
    return I18n.t('Explorer.AdvancedSearch.fields.pubmed.count', {
      count: pubmedSearch.count,
      formattedCount: NumberHelper.formatNumberWithDelimiter(pubmedSearch.count)
    });
  };

  return (
    <div className="Explorer-AdvancedSearch-field-container PubmedFilter">
      <label>
        {I18n.t('Explorer.AdvancedSearch.fields.pubmed.title')}
        <HelpLink
          href={I18n.t('Explorer.AdvancedSearch.help_uri')}
          tip={I18n.t('Explorer.AdvancedSearch.fields.pubmed.description')}
          analyticsFieldName="Pubmed Query"
        />
      </label>
      <fieldset>
        <legend>{I18n.t('Explorer.AdvancedSearch.fields.pubmed.title')}</legend>
        {loading ? (
          <div className="PubmedFilter-loading-message">{I18n.t('Explorer.AdvancedSearch.fields.pubmed.loading')}</div>
        ) : pubmedSearch ? (
          <div className="PubmedFilter-content">
            <div className="PubmedFilter-search-description">{descriptionString()}</div>
            <div className="PubmedFilter-search-edit-buttons">
              <button type="button" className="PubmedFilter-edit-button" onClick={openEditor} ref={editButtonRef}>
                {I18n.t('Explorer.AdvancedSearch.fields.pubmed.edit')}
              </button>
              <button type="button" className="PubmedFilter-clear-button" onClick={onClickClear}>
                {I18n.t('Explorer.AdvancedSearch.fields.pubmed.clear')}
              </button>
            </div>
          </div>
        ) : (
          <div className="PubmedFilter-content">
            <div className="PubmedFilter-search-create-buttons">
              <button type="button" className="PubmedFilter-add-button" onClick={openEditor} ref={newButtonRef}>
                {I18n.t('Explorer.AdvancedSearch.fields.pubmed.add')}
              </button>
            </div>
          </div>
        )}
      </fieldset>
      <dialog ref={dialogRef} onKeyDown={(e) => e.stopPropagation()}>
        {showEditor && (
          <Editor
            pubmedSearch={pubmedSearch}
            onCancel={onCancelEdit}
            onSave={onSaveEdit}
            analyticsDialogueName={ANALYTICS_DIALOGUE_NAME}
          />
        )}
      </dialog>
    </div>
  );
};

PubmedFilter.propTypes = {
  filterSet: PropTypes.instanceOf(FilterSet).isRequired
};

export default PubmedFilter;
